import React, { useEffect } from "react"
import { Card } from "react-bootstrap"
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet"
import { divIcon } from "leaflet"

// SVG pin icon — reliable, no CDN or Font Awesome dependency
const singlePinIcon = divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 40" width="28" height="40">
        <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26S28 24.5 28 14C28 6.27 21.73 0 14 0z"
              fill="#1890ff" stroke="white" stroke-width="1.5"/>
        <circle cx="14" cy="14" r="7" fill="white" opacity="0.9"/>
    </svg>`,
    className: "",
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -40],
})

interface PlaceModel {
    latitude: number
    longitude: number
    address?: string
    address2?: string
    sector?: string
    city?: string
    region?: string
    country?: string
    name?: string
}

interface Props {
    model: PlaceModel
}

const RecenterAndFix: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
    const map = useMap()
    useEffect(() => {
        if (!lat || !lng) return
        const t1 = setTimeout(() => {
            map.invalidateSize()
            map.setView([lat, lng])
        }, 100)
        const t2 = setTimeout(() => map.invalidateSize(), 400)
        const t3 = setTimeout(() => map.invalidateSize(), 1000)

        const container = map.getContainer()
        let ro: ResizeObserver | null = null
        if (typeof ResizeObserver !== "undefined" && container) {
            ro = new ResizeObserver(() => map.invalidateSize())
            ro.observe(container)
        }

        return () => {
            clearTimeout(t1)
            clearTimeout(t2)
            clearTimeout(t3)
            ro?.disconnect()
        }
    }, [lat, lng, map])
    return null
}

const getFullAddress = (model: PlaceModel): string => {
    let address = model.address ?? ""
    if (model.address2) address += ", " + model.address2
    if (model.sector) address += ", " + model.sector
    if (model.city) address += ", " + model.city
    if (model.region) address += ", " + model.region
    if (model.country) address += ", " + model.country
    return address
}

const LeafletSingleMap: React.FC<Props> = ({ model }) => {
    const lat = model.latitude
    const lng = model.longitude

    if (!lat || !lng) {
        return (
            <Card>
                <Card.Body className="text-muted text-center py-4">
                    <i className="fa-solid fa-map-location-dot me-2"></i>
                    Sin coordenadas registradas
                </Card.Body>
            </Card>
        )
    }

    return (
        <Card>
            <Card.Header className="px-2 p-1">
                <h5 className="d-inline">Mapa</h5>
                <button
                    className="btn btn-sm ms-2"
                    type="button"
                    onClick={() => {
                        window.open(
                            `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=18`,
                            "MapWindow",
                            "width=1280,height=840"
                        )
                    }}
                >
                    <i className="fa-solid fa-map-pin"></i>
                </button>
            </Card.Header>
            <Card.Body className="p-0">
                <MapContainer
                    center={[lat, lng]}
                    zoom={18}
                    style={{ width: "100%", height: "300px" }}
                    scrollWheelZoom={false}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[lat, lng]} icon={singlePinIcon} />
                    <RecenterAndFix lat={lat} lng={lng} />
                </MapContainer>
            </Card.Body>
            <Card.Footer>{getFullAddress(model)}</Card.Footer>
        </Card>
    )
}

export default LeafletSingleMap
